<template>
  <div class="d-record overflow-hidden noselect dbg"
       tabindex="0"
       :id="`record-block-${record.id}`"
       ref="drecref"
       @mouseover="onMouseOver"
       v-if="bottomY > 0"
       @mouseleave="onMouseLeave"
       :class="[
           hasOverflow ? 'hasOverflow' : '',
           showStatusColor && !fillDoctorColor ? 'dbg-'+record.iconStatus : '',
           elHeight < 15 ? 'superShort' : '',
          'record-'+record.id,
          isOnlineBooking ? 'online-booking' : '',
          isOpenedNow ? 'in-progress' : ''
       ]"
       :style="[position, doctorFill, recordTagFill, textColor]">
    <div v-if="showDoctorColor"
         class="d-record__doctor"
         :style="{ backgroundColor: doctorColor }"/>
    <div class="d-record__menu d-flex align-items-center"><MenuSvg /></div>
    <div class="d-record__body d-flex align-items-stretch position-relative w-100 h-100">
      <div class="d-record__main"
           :class="[defineBorderColor, smallWidth, smallHeight, hideIcon]">
        <span class="d-record__popover" style="pointer-events: none"><InfoSvg/></span>
        <DRecordStatusIcon class="d-record__status-icon"
                           :record="record"
                           v-click-outside="clickedOutside"
                           @touchstart.native="touchStart"
                           @click.native="stopClick"
                           :id="`popover-tgt-${record.id}`"/>
        <DRecordTime v-if="record && calendarCardShowFirst === 'date'"
                     :record="record"
                     :elWidth="elWidth"
                     :textColor="textColor"/>
        <div class="d-record__patient d-inline"
             :style="[textColor]">
          <div class="d-inline mr-1"
               v-b-tooltip.hover
               :title="`${$t('birth_day')}: ${$t(birthdaySoon)}`"
               v-if="birthdaySoon">🎂</div>
          <div class="d-inline">{{ patientName }}</div>
          <div class="planned-block"
               v-if="showPlannedServices && record.planned_services && record.planned_services.length">
            <span v-for="(ps, psind) in record.planned_services"
                  :key="`plan-s-${psind}`">{{ psind > 0 ? ', ' : ''}}{{ ps.name }}</span>
          </div>

          <div v-if="showClientTags && patient && patient.tags && patient.tags.length"
               class="d-record__services d-inline-flex ml-1">
            <div class="d-record__service"
                 v-b-tooltip.noninteractive.hover
                 v-for="(item, iind) in patient.tags"
                 :key="`patient-tag-${record.id}-${iind}`"
                 :alt="item.title"
                 :title="item.title"
                 :style="`background-color:${item.color}; border: 1px solid ${item.color == '#FFFFFF' ? '#ddd' : 'transparent'};`">
            </div>
          </div>

        </div>
        <DRecordTime v-if="record && calendarCardShowFirst === 'name'"
                     :record="record"
                     :elWidth="elWidth"
                     :textColor="textColor"/>
          <DRecordPopover :record="record"
                          ref="popover"
                          :doctor="doctor" />
      </div>
    </div>
    <DRecordResize @mouseleave.native="onMouseHover(false)"
                   @mouseover.native="onMouseHover(true)" />
  </div>
</template>

<script>
import RecordPosition from "@/components/pages/calendar/calendar/classes/RecordPosition"
import DRecordResize from "@/components/pages/calendar/calendar/DRecordResize"
import DRecordStatusIcon from "@/components/pages/calendar/calendar/DRecordStatusIcon"
import MenuSvg from '@/assets/svg-vue/calendar/more.svg'
import InfoSvg from '@/assets/svg-vue/calendar/record_info.svg'
import {mapState} from "vuex"
import {formatDate} from "@/extensions/filters/filters"
import DRecordTime from "@/components/parts/calendar/DRecordTime.vue"
import DRecordPopover from "@/components/parts/calendar/DRecordPopover.vue"
import moment from "moment/moment"

export default {
  name: "DRecord",
  components: {
    DRecordPopover,
    DRecordTime,
    DRecordStatusIcon,
    DRecordResize,
    MenuSvg,
    InfoSvg,
  },
  props: {
    record: Object,
    doctor: Object,
    pos: RecordPosition
  },
  data() {
    return {
      isOnResize: false,
      elWidth: null,
      elHeight: null,
      minElWidth: 99,
      minElHeight: 40,
      minElWidthForIcon: 50,
      hasOverflow: false,
      hoveredHeight: null
    };
  },
  computed: {
    ...mapState({
      resizableRecord: state => state.calendar.resizableRecord,
      showDoctorColor: state => state.calendar.showDoctorColor,
      showStatusColor: state => state.calendar.showStatusColor,
      showClientTags: state => state.calendar.showClientTags,
      showRecordTagColor: state => state.calendar.showRecordTagColor,
      showPlannedServices: state => state.calendar.showPlannedServices,
      calendarCardShowFirst: state => state.calendar.calendarCardShowFirst,
      fillDoctorColor: state => state.calendar.fillDoctorColor,
      isResizing: state => state.calendar.isResizing,
      clinic: state => state.auth.clinic,
    }),
    smallWidth() {
      return this.isWiderThanMinWidth ? '' : 'small-width'
    },
    smallHeight() {
      return this.isHigherThanMinHeight ? '' : 'small-height'
    },
    hideIcon() {
      return this.isSmallForIcon ? 'hide-icon' : '';
    },
    isSmallForIcon() {
      // console.log(this.pos.width, this.minElWidthForIcon, this.isHigherThanMinHeight)
      // console.log('small for icon', !this.pos.width || this.pos.width < this.minElWidthForIcon || !this.isHigherThanMinHeight)
      return !this.pos.width || this.pos.width < this.minElWidthForIcon || !this.isHigherThanMinHeight
    },
    isWiderThanMinWidth() {
      if(this.pos.width < 100 && this.elWidth) {
        return this.elWidth > 150
      }
      return this.pos.width && this.pos.width > this.minElWidth
    },
    isHigherThanMinHeight() {
      // console.log(this.elHeight, this.minElHeight)
      return this.elHeight && this.elHeight >= this.minElHeight
    },
    patientName() {
      return this.patient ? this.patient.full_name : ''
    },
    birthdaySoon() {
      const dobString = this.patient?.date_of_birth || null
      if(!dobString) return null

      const m = moment(dobString, "YYYY-MM-DD")
      const today = moment()

      m.year(today.year())

      const diff = m.startOf("day").diff(today.startOf("day"), "days")

      if (diff === 0) return 'today'
      if (diff === -1) return 'yesterday'
      if (diff === 1) return 'tomorrow'

      return null
    },
    services() {
      return this.record.services
    },
    doctorColor() {
      return this.doctor && this.doctor.color ? this.doctor.color : '#fff';
    },
    position() {
      if (!this.pos) return {}
      const height = this.pos.y < 0 ? this.pos.height + this.pos.y : this.pos.height
      return {
        width: this.pos.width + '%',
        // height: this.hoveredHeight || (this.pos.height + '%'),
        height: (this.hoveredHeight && !this.isResizing) ? this.hoveredHeight : (height + '%'),
        // maxHeight: this.pos.height + '%',
        top: Math.max(0, this.pos.y) + '%',
        left: this.pos.x + '%',
      }
    },
    bottomY() {
      return (this.pos?.y || 0) + (this.pos?.height || 0)
    },
    doctorFill() {
      if(this.fillDoctorColor && this.doctor) {
        return {
          background: this.doctorColor
        }
      }
      return {}
    },
    recordTagFill() {
      if(this.showRecordTagColor && this.record.tags?.length) {
        return {
          background: this.record.tags[0].color
        }
      }
      return {}
    },
    textColor() {
      if(!this.fillDoctorColor || !this.clinic.calendar_color) return {}
      return {
        color: this.clinic.calendar_color
      }
    },
    defineBorderColor() {
      return 'dborder-'+this.record.iconStatus
    },
    patient() {
      return this.record.patient
    },
    isOpenedNow() {
      return this.opened_visit?.id
    },
    isOnlineBooking() {
      return this.record?.record_type === 'online'
    },
  },
  methods: {
    formatDate,
    onMouseHover(bool) {
      this.record.isOnResize = bool
      this.isOnResize = bool
    },
    updateDimensions() {
      this.elWidth = this.$el.clientWidth
      this.elHeight = this.$el.clientHeight
      this.hasOverflow = this.$el.scrollHeight > this.$el.clientHeight
      setTimeout(() => {
        this.elWidth = this.$el.clientWidth
        this.elHeight = this.$el.clientHeight
        this.hasOverflow = this.$el.scrollHeight > this.$el.clientHeight
      }, 350)
    },
    stopClick() {
      this.$store.commit('setContextMenuVisible', false)
    },
    touchStart(e) {
      e.stopPropagation()
      this.$store.commit('setContextMenuVisible', false)
      this.$refs.popover.$emit('open')
    },
    clickedOutside() {
      this.$refs.popover.$emit('close')
    },
    onMouseOver() {
      if(this.hasOverflow) {
        this.hoveredHeight = this.$el.scrollHeight + 'px'
      }
    },
    onMouseLeave() {
      this.hoveredHeight = null
    }
  },
  mounted() {
    this.updateDimensions()
    window.addEventListener("resize", this.updateDimensions)
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateDimensions)
  }
}
</script>

<style scoped lang="scss">
.d-record {
  &.touchActive {
    touch-action: none;
    transform: scale(1.1);
  }
  &.online-booking {
    border: 2px solid $danger-red;
  }
  position: absolute;
  top: 0;
  //animation: gradient 4s ease infinite;
  //touch-action: none;
  background-color: white;
  border-radius: 5px;
  line-height: .9rem;
  z-index: 2;
  transition: all 0.1s ease-out;
  &:hover {
    //transform: scale(1.06);
    //transform: scale(0.96);
    opacity: 0.8;
    z-index: 3;
    left: 0 !important;
    width: 100% !important;
    .d-record__time,
    .d-record__services,
    .d-record__patient,
    .d-record__icon,
    .d-record__popover {
      display: block;
      .d-record__service {
        display: block;
      }
    }
  }
  &.hasOverflow:hover {
    opacity: 1;
  }
  .descr {
    opacity: 0.7;
  }
}
.superShort {
  .d-record__popover {
    top: -4px;
  }
  .d-record__status-icon {
    position: relative;
    bottom: 5px;
  }
  .d-record__main {
    padding: 0 10px;
  }
  &:hover {
    .d-record__popover {
      top: 0;
    }
    .d-record__main {
      padding: 5px 10px;
    }
    .d-record__status-icon {
      bottom: 0;
    }
  }
}
.dborder-blue, .dborder-confirmed {
  border: 1px solid #0787C9;
}
.dborder-red, .dborder-did_not_come, .dborder-debt {
  border: 1px solid #E30D13;
}
.dborder-yellow, .dborder-not_confirmed {
  border: 1px solid #FEC943;
}
.dborder-green, .dborder-paid, .dborder-in_chair, .dborder-came {
  border: 1px solid #5E9246;
}
.dborder-blocked {
  border: 1px solid #E5E5E5;
}
.d-record__doctor {
  position: absolute;
  left:0;
  top:0;
  bottom:0;
  width: 5px;
}
.d-record__main {
  width:100%;
  padding: 5px 10px;
  border-radius: 5px;
}
.d-record__popover {
  position:absolute;
  left: 24px;
  top: 2px;
  z-index: 1;
}
.d-record__patient {
  color: #000000;
  font-weight: bold;
  font-size: 13px;
  word-break: break-word;
  flex-shrink: 0;
}
.d-record__service {
  width: 6px;
  height: 6px;
  overflow: hidden;
  border-radius: 50%;
  margin: 5px 2px;
}
.d-record__menu {
  position: absolute;
  z-index: 2;
  right: 5px;
  top: 0;
  padding: 0px 5px;
  height: 100%;
  max-height: 42px;
  cursor: pointer;
}
.d-record__status-icon {
  float: left;
  margin-right: 10px;
  margin-bottom: 2px;
}

.noselect {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.dbg {
  background-size: 4em 4em;
  background-image: -webkit-linear-gradient(-45deg, rgba(255, 255, 255, .8) 25%, transparent 25%,
      transparent 50%, rgba(255, 255, 255, .8) 50%, rgba(255, 255, 255, .8) 75%,
      transparent 75%, transparent);
  background-image: -moz-linear-gradient(-45deg, rgba(255, 255, 255, .8) 25%, transparent 25%,
      transparent 50%, rgba(255, 255, 255, .8) 50%, rgba(255, 255, 255, .8) 75%,
      transparent 75%, transparent);
  background-image: linear-gradient(135deg, rgba(255, 255, 255, .8) 25%, transparent 25%,
      transparent 50%, rgba(255, 255, 255, .8) 50%, rgba(255, 255, 255, .8) 75%,
      transparent 75%, transparent);
}
.dbg-red, .dbg-debt, .dbg-did_not_come {
  background-color: #fcecec;
}
.dbg-yellow, .dbg-not_confirmed {
  background-color: #fffbf0;
}
.dbg-green, .dbg-paid, .dbg-came, .dbg-in_chair {
  background-color: #f2f6f0;
}
.dbg-blocked {
  background-color: #fafafa;
}
.dbg-blue, .dbg-confirmed {
  background-color: #f1fcff;
}

.small-width .d-record__time,
.small-width .d-record__patient,
.small-width .d-record__popover,
.small-width .d-record__service
{
  display: none;
}
.small-height .d-record__time,
.small-height .d-record__patient
{
  height: 15px;
  overflow: hidden;
}
.small-height {
  .d-record__service  {
    display: none;
  }
}
.hide-icon {
  .d-record__icon {
    display: none;
  }
  .d-record__popover {
    display: none;
  }
}
.planned-block {
  font-size: 12px;
  font-weight: 500;
  opacity: 0.8;
}

@media screen and (max-width: 768px) {
  .d-record__patient {
    font-size: 11px;
  }
  .planned-block {
    font-size: 10px;
    line-height: 1;
  }
}
</style>
